import * as React from 'react'

import { Icon } from '@factorialco/deprecated-design-system'
import type { Params as StaticParams } from 'static-path'

import type { navigation } from './index'
import type { Location } from './types'

type SanitizedPath<T extends string> = T extends '/'
  ? T
  : T extends `/${string}/`
    ? string
    : T extends `/${string}`
      ? string
      : T extends `${string}/`
        ? string
        : T

// FIXME: staticPath always requires one argument, even if the path has no params. This checks if
// the argument is really needed, adding a default value if it's not.
// It also accepts numbers as values of these params. `static-path` is gonna throw an error if
// something else than a string comes, so we do the String(thing) trick to be safe and accept
// string or number as a value.
export type SafeParams<Pattern extends string> = keyof StaticParams<Pattern> extends never
  ? void
  : {
      [K in keyof StaticParams<Pattern>]: string | number
    }
export type SafePath<Pattern extends string> = (params: SafeParams<Pattern>) => string

// This type extracts the current T['path] and the ParentPath and, checking if the parent is just
// `/` or it has information, returns the whole build path. This is because the first location path
// is tipically `/`, so we don't want to join with the next one (obtaining `//firstPath`).
export type PathExtractor<T extends Location, ParentPath extends string> = ParentPath extends '/'
  ? SanitizedPath<T['path']>
  : `${ParentPath}/${SanitizedPath<T['path']>}`

// This transforms the Location into the new BuiltLocation type, accessible by devs. It won't
// have any children since these will be just added at the parent, and have a new method called
// fullPath to build paths.
export type BuiltLocation<T extends Location, ParentPath extends string> = Omit<
  Location,
  'children' | 'path' | 'level' | 'size'
> & {
  fullPath: SafePath<`${PathExtractor<T, ParentPath>}`>
  path: T['path']
  level: T['level'] extends unknown ? undefined : T['level']
  size: T['size'] extends unknown ? undefined : T['size']
}

// This iterates recursively the node.children elements. If there's some, we add them to the parent.
export type LocationBuilder<T extends Location, ParentPath extends string = ''> = T extends {
  children: Record<infer U, unknown>
}
  ? BuiltLocation<T, ParentPath> & {
      [Key in U]: LocationBuilder<T['children'][Key], PathExtractor<T, ParentPath>>
    }
  : BuiltLocation<T, ParentPath>

type NavigationType<T> = Awaited<T>

export type Navigation<T extends Location> = LocationBuilder<
  NavigationType<T>,
  NavigationType<T>['path']
>

export type Groups = 'main' | 'you' | 'company'

export interface Section {
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  name?: string
}

export interface NavigationAction {
  section: Section
  location: BuiltLocation<Location, string>
}

export type ComputedLocation = Pick<Location, 'path'> & {
  children: Record<Exclude<string, keyof Location>, Location>
} & Pick<BuiltLocation<Location, string>, 'fullPath'>

export type ComputedFilteredLocation = Omit<ComputedLocation, 'children'> & {
  children: ComputedLocation[]
}

export interface NavigationContext<T extends Location> {
  navigation: Navigation<T>
}

export type NavigationPaths = typeof navigation

export const navigationContext = React.createContext<NavigationContext<NavigationPaths> | null>(
  null
)
